import { useEffect, useRef, memo, useMemo, useCallback, useState } from "react";
import DataGrid, {
  Column,
  Format,
  Grouping,
  GroupPanel,
  Paging,
  SearchPanel,
  Summary,
  GroupItem,
  Export,
  FilterRow,
  HeaderFilter,
  Pager,
  Selection,
  Item,
  Toolbar,
  StateStoring,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";
import SelectBox from "devextreme-react/select-box";

import { useFetch } from "../../../hooks/useFetch";
// import "./run-list.css"
import {
  gradeMetricForRun,
  gradeMetricForSpatialRun,
} from "src/utils/run-grading";
import { KIT_TYPE_TISSUE, WELL_LAYOUT_PANOS_SLIDE } from "./well-layout";

const cnstMode = { mode: "single" };
const fields = [
  { name: "Last Week", value: -7 },
  { name: "Last Month", value: 1 },
  { name: "Last 3 Months", value: 3 },
  { name: "Last 6 Months", value: 6 },
  { name: "Last Year", value: 12 },
  { name: "All", value: 0 },
];

const customizeTextUpTime = (e) => {
  if (e.value <= 0) return "";
  const hours = e.value / 3600;
  const days = hours / 24;
  return days.toFixed(2) + " days";
};

const groupCellTemplate = (elem, data) => {
  var formattedValue = `${data.displayValue} (${data.data.aggregates} runs)   ${
    data.groupContinuesMessage ? `  (${data.groupContinuesMessage})` : ""
  }`;
  elem.append(formattedValue);
};

const renderGridCell = (data) => {
  return (
    <div>
      <div
        className="badge"
        onClick={() => alert(data.data.runID)}
        style={{
          display:
            data.data.runID === "6345e6054e6fad0dd71522e1" ? null : "none",
        }}
      >
        10
      </div>
      <div>{data.text}</div>
    </div>
  );
};

const pagesizes = [20, 100, "all"];

const colfixing = { enabled: true };

const customizedDateText = (e) => {
  if (e.value.getFullYear() < 2020) return "";
  return e.valueText;
};

const customizeBatchText = (e) => {
  let s = "";
  for (let i in e.value) {
    s += `(Batch Name: ${e.value[i].Name}, PMG mask: ${e.value[i].PMGMask}, cycles: ${e.value[i].Cycles})\n`;
  }
  return s;
};

// Pre-computed well positions (normalized 0-1) derived from tile layout coordinates.
// All layouts share the same physical flow cell (~19mm x 55mm, ratio ~1:2.9).
// Well bounding boxes are computed from the min/max tile XY + tile dimensions (1.763 x 2.638 mm).
const FLOW_CELL_WELLS = {
  OneWell: [{ loc: "A1", x: 0, y: 0, w: 1, h: 1 }],
  [WELL_LAYOUT_PANOS_SLIDE]: [{ loc: "A1", x: 0, y: 0, w: 1, h: 1 }],
  TwelveWell: [
    { loc: "A1", x: 0.605, y: 0, w: 0.395, h: 0.1496 },
    { loc: "A2", x: 0, y: 0, w: 0.395, h: 0.1496 },
    { loc: "B1", x: 0.605, y: 0.1701, w: 0.395, h: 0.1496 },
    { loc: "B2", x: 0, y: 0.1701, w: 0.395, h: 0.1496 },
    { loc: "C1", x: 0.605, y: 0.3402, w: 0.395, h: 0.1496 },
    { loc: "C2", x: 0, y: 0.3402, w: 0.395, h: 0.1496 },
    { loc: "D1", x: 0.605, y: 0.5103, w: 0.395, h: 0.1496 },
    { loc: "D2", x: 0, y: 0.5103, w: 0.395, h: 0.1496 },
    { loc: "E1", x: 0.605, y: 0.6803, w: 0.395, h: 0.1496 },
    { loc: "E2", x: 0, y: 0.6803, w: 0.395, h: 0.1496 },
    { loc: "F1", x: 0.605, y: 0.8504, w: 0.395, h: 0.1496 },
    { loc: "F2", x: 0, y: 0.8504, w: 0.395, h: 0.1496 },
  ],
  FortyEightWell: [
    { loc: "A1", x: 0.8097, y: 0, w: 0.1903, h: 0.0506 },
    { loc: "A2", x: 0.5398, y: 0, w: 0.1903, h: 0.0506 },
    { loc: "A3", x: 0.2699, y: 0, w: 0.1903, h: 0.0506 },
    { loc: "A4", x: 0, y: 0, w: 0.1903, h: 0.0506 },
    { loc: "B1", x: 0.8097, y: 0.0863, w: 0.1903, h: 0.0506 },
    { loc: "B2", x: 0.5398, y: 0.0863, w: 0.1903, h: 0.0506 },
    { loc: "B3", x: 0.2699, y: 0.0863, w: 0.1903, h: 0.0506 },
    { loc: "B4", x: 0, y: 0.0863, w: 0.1903, h: 0.0506 },
    { loc: "C1", x: 0.8097, y: 0.1726, w: 0.1903, h: 0.0506 },
    { loc: "C2", x: 0.5398, y: 0.1726, w: 0.1903, h: 0.0506 },
    { loc: "C3", x: 0.2699, y: 0.1726, w: 0.1903, h: 0.0506 },
    { loc: "C4", x: 0, y: 0.1726, w: 0.1903, h: 0.0506 },
    { loc: "D1", x: 0.8097, y: 0.2589, w: 0.1903, h: 0.0506 },
    { loc: "D2", x: 0.5398, y: 0.2589, w: 0.1903, h: 0.0506 },
    { loc: "D3", x: 0.2699, y: 0.2589, w: 0.1903, h: 0.0506 },
    { loc: "D4", x: 0, y: 0.2589, w: 0.1903, h: 0.0506 },
    { loc: "E1", x: 0.8097, y: 0.3452, w: 0.1903, h: 0.0506 },
    { loc: "E2", x: 0.5398, y: 0.3452, w: 0.1903, h: 0.0506 },
    { loc: "E3", x: 0.2699, y: 0.3452, w: 0.1903, h: 0.0506 },
    { loc: "E4", x: 0, y: 0.3452, w: 0.1903, h: 0.0506 },
    { loc: "F1", x: 0.8097, y: 0.4315, w: 0.1903, h: 0.0506 },
    { loc: "F2", x: 0.5398, y: 0.4315, w: 0.1903, h: 0.0506 },
    { loc: "F3", x: 0.2699, y: 0.4315, w: 0.1903, h: 0.0506 },
    { loc: "F4", x: 0, y: 0.4315, w: 0.1903, h: 0.0506 },
    { loc: "G1", x: 0.8097, y: 0.5179, w: 0.1903, h: 0.0506 },
    { loc: "G2", x: 0.5398, y: 0.5179, w: 0.1903, h: 0.0506 },
    { loc: "G3", x: 0.2699, y: 0.5179, w: 0.1903, h: 0.0506 },
    { loc: "G4", x: 0, y: 0.5179, w: 0.1903, h: 0.0506 },
    { loc: "H1", x: 0.8097, y: 0.6042, w: 0.1903, h: 0.0506 },
    { loc: "H2", x: 0.5398, y: 0.6042, w: 0.1903, h: 0.0506 },
    { loc: "H3", x: 0.2699, y: 0.6042, w: 0.1903, h: 0.0506 },
    { loc: "H4", x: 0, y: 0.6042, w: 0.1903, h: 0.0506 },
    { loc: "I1", x: 0.8097, y: 0.6905, w: 0.1903, h: 0.0506 },
    { loc: "I2", x: 0.5398, y: 0.6905, w: 0.1903, h: 0.0506 },
    { loc: "I3", x: 0.2699, y: 0.6905, w: 0.1903, h: 0.0506 },
    { loc: "I4", x: 0, y: 0.6905, w: 0.1903, h: 0.0506 },
    { loc: "J1", x: 0.8097, y: 0.7768, w: 0.1903, h: 0.0506 },
    { loc: "J2", x: 0.5398, y: 0.7768, w: 0.1903, h: 0.0506 },
    { loc: "J3", x: 0.2699, y: 0.7768, w: 0.1903, h: 0.0506 },
    { loc: "J4", x: 0, y: 0.7768, w: 0.1903, h: 0.0506 },
    { loc: "K1", x: 0.8097, y: 0.8631, w: 0.1903, h: 0.0506 },
    { loc: "K2", x: 0.5398, y: 0.8631, w: 0.1903, h: 0.0506 },
    { loc: "K3", x: 0.2699, y: 0.8631, w: 0.1903, h: 0.0506 },
    { loc: "K4", x: 0, y: 0.8631, w: 0.1903, h: 0.0506 },
    { loc: "L1", x: 0.8097, y: 0.9494, w: 0.1903, h: 0.0506 },
    { loc: "L2", x: 0.5398, y: 0.9494, w: 0.1903, h: 0.0506 },
    { loc: "L3", x: 0.2699, y: 0.9494, w: 0.1903, h: 0.0506 },
    { loc: "L4", x: 0, y: 0.9494, w: 0.1903, h: 0.0506 },
  ],
};

// Fixed flow cell display size (pixels). All layouts render at the same size.
const FC_WIDTH = 65;
const FC_HEIGHT = 190; // aspect ratio ~1:2.9, matching real flow cell proportions
const FC_PAD = 3; // inner padding

// Flow cell well map - renders wells at their real physical positions
const FlowCellWellMap = memo(({ wells, wellLayout }) => {
  const wellDefs = FLOW_CELL_WELLS[wellLayout] || FLOW_CELL_WELLS.TwelveWell;

  const activeWells = useMemo(() => {
    const map = new Map();
    if (wells) {
      wells.forEach((w) => map.set(w.WellLocation, w.WellLabel || ""));
    }
    return map;
  }, [wells]);

  const innerW = FC_WIDTH - FC_PAD * 2;
  const innerH = FC_HEIGHT - FC_PAD * 2;

  return (
    <div
      style={{
        position: "relative",
        width: FC_WIDTH,
        height: FC_HEIGHT,
        backgroundColor: "#f5f5f5",
        border: "1.5px solid #bbb",
        borderRadius: 6,
        padding: FC_PAD,
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {wellDefs.map((wd) => {
        const isActive = activeWells.has(wd.loc);
        const label = activeWells.get(wd.loc);
        const pxW = Math.max(wd.w * innerW - 1, 2);
        const pxH = Math.max(wd.h * innerH - 1, 2);

        return (
          <div
            key={wd.loc}
            title={isActive ? `${wd.loc}${label ? `: ${label}` : ""}` : wd.loc}
            style={{
              position: "absolute",
              left: FC_PAD + wd.x * innerW,
              top: FC_PAD + wd.y * innerH,
              width: pxW,
              height: pxH,
              borderRadius: 2,
              backgroundColor: isActive ? "#26A69A" : "#e4e4e4",
              border: `1px solid ${isActive ? "#00897B" : "#d0d0d0"}`,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {/* Show well label inside if the well is large enough */}
            {isActive && pxH > 14 && (
              <span
                style={{
                  fontSize: Math.min(pxH * 0.45, 10),
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                {wd.loc}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
});

const renderWellsCell = (cellData) => {
  const wells = cellData.value;
  // Same inference as the detail views: a stored wellLayout is always honored;
  // without one, tissue runs are PanosSlide rather than the 12-well default.
  const wellLayout =
    cellData.data?.wellLayout ||
    (cellData.data?.kitType === KIT_TYPE_TISSUE
      ? WELL_LAYOUT_PANOS_SLIDE
      : undefined);
  if (!wells || wells.length === 0)
    return <span style={{ color: "#999" }}>--</span>;
  return <FlowCellWellMap wells={wells} wellLayout={wellLayout} />;
};

const numItemsInArray = (e) => {
  if (!e.value) {
    return "--";
  }
  return `${e.value.length}`;
};

export default memo((props) => {
  const [numMonths, setNumMonths] = useState(-7);
  const {
    data: runs,
    isLoading,
    isFetching,
    refetch,
  } = useFetch(
    "/spatialRun?numMonths=" +
      numMonths +
      (props.customerOnly ? "&customer" : ""),
  );
  const grid = useRef();
  const [refetchRequested, setRefetchRequested] = useState(false);

  useEffect(() => {
    if (!isLoading && runs) {
      const params = new URLSearchParams(
        window.location.hash
          .replace("#/spatialRuns", "")
          .replace("#/customerSpatialRuns", ""),
      );
      const runID = params.get("r");
      if (runID !== "null" && grid && grid.current && grid.current.instance) {
        grid.current.instance.selectRows([runID], false);
        grid.current.instance.navigateToRow(runID);
      }
    }
  }, [isLoading, runs]);

  const onSelectionChanged = useCallback(
    (e) => {
      console.log("selected run", e.selectedRowsData[0]);
      props.runSelected(e.selectedRowsData[0]);
    },
    [props],
  );

  const refreshOptions = useMemo(() => {
    return {
      icon: "refresh",
      hint: "Refresh",
      text: "",
      stylingMode: "icon",
      onClick: () => {
        setRefetchRequested(true);
        refetch().then(() => setRefetchRequested(false));
      },
    };
  }, [refetch]);

  const resetOptions = useMemo(() => {
    return {
      hint: "Reset to default table layout",
      text: "Reset",
      stylingMode: "text",
      onClick: () => {
        grid.current.instance.state({});
      },
    };
  }, []);

  const customizeText = useCallback((e) => {
    if (e.value <= 0) return "";
    return e.valueText;
  }, []);

  const cellPrepared = useCallback((e) => {
    if (e.rowType !== "data") return;
    const clr = gradeMetricForSpatialRun(
      e.column.name,
      e.value,
      // e.data?.panelPartNumber
    );

    e.cellElement.style.backgroundColor = clr;
    if (!clr) e.cellElement.style.color = "black";
    else e.cellElement.style.color = "white";
  }, []);

  // The latest datagrid implementation with embedded refresh toolbar icon
  return (
    <div id="parentDivList" style={{ margin: 10 }}>
      <SelectBox
        value={numMonths}
        valueExpr="value"
        displayExpr="name"
        placeholder="Select a value..."
        showClearButton={false}
        dataSource={fields}
        text="Data to query"
        onValueChanged={(v) => {
          setNumMonths(v.value);
          grid.current.instance.getDataSource().reload();
        }}
        style={{ width: "150px" }}
      />

      <DataGrid
        ref={grid}
        className="mygrid"
        dataSource={runs ?? []}
        width="100%"
        height="90vh"
        keyExpr="runID"
        wordWrapEnabled
        showBorders
        showColumnLines
        showRowLines
        selection={cnstMode}
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        columnHidingEnabled
        columnAutoWidth
        columnFixing={colfixing}
        onCellPrepared={cellPrepared}
        onSelectionChanged={onSelectionChanged}
      >
        <Export enabled fileName={"RunsTable"} allowExportSelectedData={true} />
        <GroupPanel visible />
        <SearchPanel visible />
        <Grouping autoExpandAll />
        <FilterRow visible />
        <HeaderFilter visible />
        <Selection mode={"single"} />
        <StateStoring
          enabled={false}
          type="localStorage"
          storageKey="runsTablev5"
        />

        <Toolbar>
          <Item location="after" widget="dxButton" options={refreshOptions} />
          <Item name="columnChooserButton" />
          <Item name="groupPanel" />
          <Item name="searchPanel" />
          <Item name="exportButton" />
          {/* <Item location="before" widget="dxButton" options={spacerOptions} /> */}
          <Item location="before" widget="dxButton" options={resetOptions} />
        </Toolbar>

        <Column
          fixed
          dataField="runName"
          dataType="string"
          cellRender={renderGridCell}
        />
        <Column
          dataField="instrumentName"
          caption="instrument"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="instrumentNickname"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="Date"
          dataType={"date"}
          format={"MMM dd yyyy HH:mm"}
        />

        <Column dataField="side" />
        <Column
          dataField="lastStatusUpdated"
          dataType={"date"}
          format={"MMM dd yyyy HH:mm"}
        />
        <Column dataField="lastStatus" />

        <Column
          dataField="systemUpTimeInSeconds"
          allowEditing={false}
          customizeText={customizeTextUpTime}
          caption="System Up Time"
        />

        <Column dataField="panelName" />
        <Column dataField="kitType" />
        <Column
          dataField="runTime"
          caption="Run Time (Hrs)"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="fixedPoint" precision={2} />
        </Column>

        <Column dataField="recipeBundleName" allowEditing={false} />

        <Column dataField="tenantID" width={150} />
        <Column dataField="version" />
        <Column dataField="runFolderName" />

        <Column dataField="tileSelectionModule" />
        <Column
          dataField="batches"
          customizeText={numItemsInArray}
          caption="Num. Batches"
        />
        <Column
          dataField="batches"
          customizeText={customizeBatchText}
          width={300}
        />
        <Column dataField="wellLayout" dataType="string" />
        <Column
          dataField="wells"
          customizeText={numItemsInArray}
          caption="Num. Wells"
        />
        <Column
          dataField="wells"
          cellRender={renderWellsCell}
          caption="Wells"
          width={100}
          allowFiltering={false}
          allowSorting={false}
        />
        <Column dataField="cellConfluencyRate" customizeText={customizeText}>
          <Format type="percent" precision={2} />
        </Column>
        <Column dataField="cellCount" customizeText={customizeText} />
        <Column dataField="averageAssigned" customizeText={customizeText}>
          <Format type="percent" precision={2} />
        </Column>
        <Column dataField="flowcellSerialNumber" />
        <Column dataField="flowcellPartNumber" />
        <Column dataField="flowcellLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="flowcellExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />

        <Column dataField="panelSerialNumber" />
        <Column dataField="panelPartNumber" />
        <Column dataField="panelLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="panelExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />

        <Column dataField="bufferSerialNumber" />
        <Column dataField="bufferPartNumber" />
        <Column dataField="bufferLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="bufferExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />

        <Summary>
          <GroupItem
            column="instrumentName"
            summaryType="count"
            displayFormat="{0} runs"
          />
        </Summary>

        <Paging defaultPageSize={100} />
        <Pager
          showPageSizeSelector={true}
          allowedPageSizes={pagesizes}
          showInfo={true}
          showNavigationButtons={true}
        />
      </DataGrid>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: "#parentDivList" }}
        visible={isFetching || refetchRequested}
        showIndicator={true}
        shading={true}
        // showPane={true}
      />
    </div>
  );
});
